"use client";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { docsNav } from "@/lib/docs-nav";

export function DocsSidebar() {
  const [activeId, setActiveId] = useState<string>("introduction");
  const flatIds = useRef(docsNav.flatMap((g) => g.links.map((l) => l.href.split("#")[1])));

  useEffect(() => {
    const sections = flatIds.current
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (sections.length === 0) return;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const next = flatIds.current.find((id) => visible.has(id));
        if (next) setActiveId(next);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <nav aria-label="Documentation" className="sticky top-20 max-h-[calc(100dvh-5rem)] overflow-y-auto pb-10 pr-4">
      {docsNav.map((group) => (
        <div key={group.group} className="mb-6">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            {group.group}
          </p>
          <ul className="space-y-0.5">
            {group.links.map((link) => {
              const id = link.href.split("#")[1];
              const active = id === activeId;
              return (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className={`block rounded-lg px-3 py-2 text-sm transition ${
                      active
                        ? "bg-brand-50 font-semibold text-brand-700"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                    aria-current={active ? "page" : undefined}
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
